import { z } from 'zod';
import { DIRECTORY_WEB_PHOTO_PATH_PATTERN, directoryFacetsDTOSchema, directoryInviteEveryonePreviewDTOSchema, directoryInviteEveryoneResultDTOSchema, directoryMeDTOSchema, directoryOrgChartDTOSchema, directoryPersonAccessDTOSchema, directoryPersonSummaryDTOSchema, directoryProfileDTOSchema, myDirectoryDetailsDTOSchema, directorySettingsDTOSchema, } from '@instanexus/contracts';
import { dataEnvelopeSchema, listEnvelopeSchema, WEB_API_PREFIX } from '../transport.js';
const listPeopleSchema = listEnvelopeSchema(directoryPersonSummaryDTOSchema);
const meSchema = dataEnvelopeSchema(directoryMeDTOSchema);
const facetsSchema = dataEnvelopeSchema(directoryFacetsDTOSchema);
const settingsSchema = dataEnvelopeSchema(directorySettingsDTOSchema);
const profileSchema = dataEnvelopeSchema(directoryProfileDTOSchema);
const myDetailsSchema = dataEnvelopeSchema(myDirectoryDetailsDTOSchema);
const orgChartSchema = dataEnvelopeSchema(directoryOrgChartDTOSchema);
const personAccessSchema = dataEnvelopeSchema(directoryPersonAccessDTOSchema);
const inviteEveryonePreviewSchema = dataEnvelopeSchema(directoryInviteEveryonePreviewDTOSchema);
const inviteEveryoneResultSchema = dataEnvelopeSchema(directoryInviteEveryoneResultDTOSchema);
/** A blank search is never sent: `?q=` would be a different request from no `q`. */
function nonEmpty(value) {
    return value === undefined || value.trim() === '' ? undefined : value;
}
export function createEmployeeDirectoryResource(transport) {
    return {
        async listPeople(params = {}) {
            return transport.request({
                path: `${WEB_API_PREFIX}/directory/people`,
                schema: listPeopleSchema,
                query: {
                    page: params.page,
                    pageSize: params.pageSize,
                    q: nonEmpty(params.q),
                    locationId: params.locationId,
                    departmentId: params.departmentId,
                },
                signal: params.signal,
            });
        },
        async me() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/me`,
                schema: meSchema,
            });
            return envelope.data;
        },
        async facets(params = {}) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/facets`,
                schema: facetsSchema,
                query: { q: nonEmpty(params.q), locationId: params.locationId, departmentId: params.departmentId },
                signal: params.signal,
            });
            return envelope.data;
        },
        async settings() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/settings`,
                schema: settingsSchema,
            });
            return envelope.data;
        },
        async updateSettings(input) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/settings`,
                schema: settingsSchema,
                method: 'PATCH',
                body: input,
            });
            return envelope.data;
        },
        async person(id, options) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/people/${encodeURIComponent(id)}`,
                schema: profileSchema,
                signal: options?.signal,
            });
            return envelope.data;
        },
        async orgChart(id, options) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/people/${encodeURIComponent(id)}/org`,
                schema: orgChartSchema,
                signal: options?.signal,
            });
            return envelope.data;
        },
        async personAccess(id) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/people/${encodeURIComponent(id)}/access`,
                schema: personAccessSchema,
            });
            return envelope.data;
        },
        async grantPersonAccess(id) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/people/${encodeURIComponent(id)}/access`,
                schema: personAccessSchema,
                method: 'POST',
                body: {},
            });
            return envelope.data;
        },
        async uploadMyPhoto(photo, contentType) {
            const envelope = await transport.requestUpload({
                path: `${WEB_API_PREFIX}/directory/me/photo`,
                schema: meSchema,
                contentType,
                body: photo,
            });
            return envelope.data;
        },
        async removeMyPhoto() {
            await transport.request({ method: 'DELETE', path: `${WEB_API_PREFIX}/directory/me/photo`, schema: z.undefined() });
        },
        async removePersonPhoto(id) {
            await transport.request({
                method: 'DELETE',
                path: `${WEB_API_PREFIX}/directory/people/${encodeURIComponent(id)}/photo`,
                schema: z.undefined(),
            });
        },
        async updateMyDetails(input) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/me`,
                schema: myDetailsSchema,
                method: 'PATCH',
                body: input,
            });
            return envelope.data;
        },
        async inviteEveryonePreview() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/invite-everyone`,
                schema: inviteEveryonePreviewSchema,
            });
            return envelope.data;
        },
        async inviteEveryone() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/directory/invite-everyone`,
                schema: inviteEveryoneResultSchema,
                method: 'POST',
                body: {},
            });
            return envelope.data;
        },
        async photo(photoPath, options) {
            // The path comes from a DTO: never fetch (with the user's token) anything but a directory photo.
            if (!DIRECTORY_WEB_PHOTO_PATH_PATTERN.test(photoPath))
                throw new TypeError('not a directory photo path');
            return transport.requestBlob({
                path: photoPath,
                accept: 'image/*',
                query: { size: options?.size },
                signal: options?.signal,
            });
        },
    };
}
//# sourceMappingURL=employee-directory.js.map